import {
    defineComponent,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    watch,
} from "vue";
import {
    defaultKeymap,
    history,
    historyKeymap,
} from "@codemirror/commands";
import {
    acceptCompletion,
    autocompletion,
    closeBrackets,
    closeBracketsKeymap,
    completionKeymap,
    completionStatus,
    startCompletion,
} from "@codemirror/autocomplete";
import { EditorState } from "@codemirror/state";
import {
    drawSelection,
    EditorView,
    keymap,
    lineNumbers,
    placeholder,
} from "@codemirror/view";

import { logSearchCompletionSource } from "./completion.js";
import { logSearchDiagnosticsExtension } from "./diagnostics.js";
import { logSearchHighlightExtension } from "./highlight.js";

export const LogSearchEditor = defineComponent({
    name: "LogSearchEditor",

    props: {
        value: {
            type: String,
            default: "",
        },

        placeholder: {
            type: String,
            default: `type == "message_delete" and contains(extra.content, "spam")`,
        },

        onupdate: {
            type: Function as PropType<(value: string) => void>,
            required: true,
        },

        onsubmit: {
            type: Function as PropType<() => void>,
            default: null,
        },

        onexamples: {
            type: Function as PropType<() => void>,
            default: null,
        },

        onbookmarks: {
            type: Function as PropType<() => void>,
            default: null,
        },

        readonly: {
            type: Boolean,
            default: false,
        },

        compact: {
            type: Boolean,
            default: false,
        },

        autoHeight: {
            type: Boolean,
            default: false,
        },

        footer: {
            type: Boolean,
            default: true,
        },
    },

    setup(props) {
        const container = ref<HTMLDivElement | null>(null);
        let editor: EditorView | null = null;
        const editorMinHeight = props.autoHeight ? "0" : props.compact ? "76px" : "96px";
        const editorFontFamily = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
        const editorFontSize = "14px";
        const editorLineHeight = "21.7px";

        function getValue() {
            return editor?.state.doc.toString() ?? "";
        }

        function setValue(nextValue: string) {
            if (!editor || getValue() === nextValue)
                return;

            editor.dispatch({
                changes: {
                    from    : 0,
                    to      : editor.state.doc.length,
                    insert  : nextValue,
                },
            });
        }

        function shouldStartCompletion(view: EditorView) {
            const position = view.state.selection.main.head;
            const before = view.state.sliceDoc(0, position);
            const tail = before.slice(Math.max(0, before.length - 240));

            return tail.trim() === ""
                || /\btype\s*(?:==|eq|!=|ne)\s*(?:"[^"\n]*)?$/.test(tail)
                || /\btype\s+(?:in|not\s+in)\s*\{\s*(?:(?:"[^"]*"\s*,\s*)*)?(?:"[^"\n]*)?$/.test(tail)
                || /(?:^|[\s({,])(?:and|or|not)\s*$/.test(tail)
                || /(?:^|[\s({,])@[a-zA-Z0-9_:"]*$/.test(tail)
                || /(?:^|[\s({,])[a-zA-Z_][a-zA-Z0-9_.:[\]*]*$/.test(tail);
        }

        onMounted(() => {
            if (!container.value)
                return;

            editor = new EditorView({
                parent: container.value,
                state: EditorState.create({
                    doc: props.value,
                    extensions: [
                        lineNumbers(),
                        history(),
                        drawSelection(),
                        closeBrackets(),
                        EditorState.readOnly.of(props.readonly),
                        EditorView.editable.of(!props.readonly),
                        placeholder(props.placeholder),
                        logSearchHighlightExtension,
                        logSearchDiagnosticsExtension,
                        autocompletion({
                            override        : [ logSearchCompletionSource ],
                            activateOnTyping: true,
                            defaultKeymap   : false,
                            icons           : true,
                            maxRenderedOptions: 12,
                        }),
                        keymap.of([
                            {
                                key: "Ctrl-Space",
                                run: startCompletion,
                            },
                            {
                                key: "Tab",
                                run: acceptCompletion,
                            },
                            {
                                key: "Mod-Enter",
                                run() {
                                    props.onsubmit?.();
                                    return true;
                                },
                            },
                            ...completionKeymap,
                            ...closeBracketsKeymap,
                            ...historyKeymap,
                            ...defaultKeymap,
                        ]),
                        EditorView.lineWrapping,
                        EditorView.updateListener.of((update) => {
                            if (update.docChanged) {
                                props.onupdate(update.state.doc.toString());

                                if (!props.readonly && completionStatus(update.state) === null && shouldStartCompletion(update.view))
                                    requestAnimationFrame(() => startCompletion(update.view));
                            }
                        }),
                        EditorView.theme({
                            "&": {
                                minHeight: editorMinHeight,
                            },
                            ".cm-content": {
                                minHeight: editorMinHeight,
                                padding: ".75rem .85rem",
                                caretColor: "#fff",
                                fontFamily: editorFontFamily,
                                fontSize: editorFontSize,
                                lineHeight: editorLineHeight,
                            },
                            ".cm-scroller": {
                                fontFamily: editorFontFamily,
                                minHeight: editorMinHeight,
                            },
                            ".cm-line": {
                                padding: "0 0.25rem",
                                lineHeight: editorLineHeight,
                            },
                            ".cm-gutters": {
                                backgroundColor: "#0d0d0d",
                                color: "#666",
                                borderRight: "1px solid #252525",
                                minHeight: editorMinHeight,
                                padding: "0",
                            },
                            ".cm-gutter": {
                                minHeight: editorMinHeight,
                            },
                            ".cm-lineNumbers .cm-gutterElement": {
                                boxSizing: "border-box",
                                fontFamily: editorFontFamily,
                                fontSize: editorFontSize,
                                lineHeight: editorLineHeight,
                                padding: "0 .55rem",
                            },
                            ".cm-activeLineGutter": {
                                backgroundColor: "#171717",
                                color: "#aaa",
                            },
                            ".cm-activeLine": {
                                backgroundColor: "rgba(255,255,255,.035)",
                            },
                            ".cm-cursor": {
                                borderLeftColor: "#fff",
                            },
                            ".cm-selectionBackground, &.cm-focused .cm-selectionBackground": {
                                backgroundColor: "rgba(32, 169, 244, .28)",
                            },
                            ".cm-logsearch-field": {
                                color: "#8ecbff",
                            },
                            ".cm-logsearch-keyword": {
                                color: "#c9a4ff",
                            },
                            ".cm-logsearch-string": {
                                color: "#9bdd9b",
                            },
                            ".cm-logsearch-operator, .cm-logsearch-punctuation": {
                                color: "#ffcf80",
                            },
                            ".cm-logsearch-function": {
                                color: "#ff9f9f",
                            },
                            ".cm-logsearch-placeholder-token": {
                                color: "#f0abfc",
                                textDecoration: "underline",
                                textDecorationColor: "rgba(240, 171, 252, .45)",
                            },
                            ".cm-logsearch-identifier": {
                                color: "#d7d7d7",
                            },
                            "&.cm-focused": {
                                outline: "1px solid color-mix(in srgb, var(--primary) 55%, #333)",
                            },
                        }),
                    ],
                }),
            });
        });

        watch(() => props.value, (nextValue) => setValue(nextValue));

        onBeforeUnmount(() => {
            editor?.destroy();
            editor = null;
        });

        return () => (
            <div class={{
                "log-search-editor": true,
                "log-search-editor-readonly": props.readonly,
            }}>
                <div ref={container} />
                {props.footer && <div class="log-search-editor-footer">
                    <span><kbd>Ctrl</kbd> + <kbd>Space</kbd> suggestions</span>
                    <span><kbd>Tab</kbd> accepter</span>
                    <span><kbd>Ctrl</kbd> + <kbd>Entrée</kbd> rechercher</span>
                    <button type="button" class="log-search-editor-action log-search-editor-bookmarks" onClick={() => props.onbookmarks?.()}>
                        <span class="material-symbols-outlined">bookmark</span>
                        Bookmarks
                    </button>
                    <button type="button" class="log-search-editor-action" onClick={() => props.onexamples?.()}>
                        <span class="material-symbols-outlined">view_list</span>
                        Exemples
                    </button>
                </div>}
            </div>
        );
    },
});

export default LogSearchEditor;
